import { useState } from 'react'
import styled from 'styled-components'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import SideSheetModal from 'shared/components/SideSheetModal'
import { RaffleDetail } from 'shared/types/raffles'
import { RaffleEntryStoreStep } from './RaffleEntryStoreStep'
import { RaffleEntryDetailsStep } from './RaffleEntryDetailsStep'
import { RaffleEntryConfirmation } from './RaffleEntryConfirmation'
import { CloseX } from 'shared/components/Icons/SVGIcons/CloseX'
import { ChevronDown } from 'shared/components/Icons/SVGIcons/ChevronDown'
import { colors } from 'shared/lib'
import { t } from 'localization'
import { ProductDataItem } from 'shared/types/ConsumerSearch'

const TP = 'shared.components.Raffles.RaffleEntryModal'

type RaffleEntryModalProps = {
  raffle: RaffleDetail
  onClose: () => void
}

export const RaffleEntryModal = ({ raffle, onClose }: RaffleEntryModalProps) => {
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [selectedStore, setSelectedStore] = useState<string | null>(null)
  const [selectedSize, setSelectedSize] = useState<string | null>(null)
  const [trendingProducts, setTrendingProducts] = useState<ProductDataItem[]>([])

  const handleContinueToDetails = () => {
    sendTrackingEvent('DRAWING_ENTRY_CONTINUE_TAP', {
      product_template_slug: raffle.slug,
      store_name: selectedStore,
      size: selectedSize,
    })
    setStep(2)
  }

  return (
    <SideSheetModal
      isOpen
      animated
      anchor="right"
      mobileAnchor="bottom"
      mobileFullScreen
      overlayVariant="dark"
      hasPadding={false}
      noBorder
      onClose={onClose}
    >
      <ModalInner data-qa="raffle-entry-modal">
        {step !== 3 && (
          <ModalHeader>
            <HeaderSide>
              {step === 2 && (
                <BackButton onClick={() => setStep(1)} data-qa="raffle-entry-back" aria-label="Back">
                  <ChevronDown color={colors.FC2_BLACK} width="12" height="12" />
                </BackButton>
              )}
            </HeaderSide>
            <HeaderTitle>
              {step === 1 && t(`${TP}.enterRaffle`, 'Enter Drawing')}
              {step === 2 && t(`${TP}.entryDetails`, 'Entry Details')}
            </HeaderTitle>
            <HeaderSide>
              <CloseButton onClick={onClose} data-qa="raffle-entry-close" aria-label="Close">
                <CloseX color={colors.FC2_BLACK} width="12" height="12" />
              </CloseButton>
            </HeaderSide>
          </ModalHeader>
        )}

        {step === 1 && (
          <RaffleEntryStoreStep
            raffle={raffle}
            selectedStore={selectedStore}
            selectedSize={selectedSize}
            onStoreSelect={setSelectedStore}
            onSizeSelect={setSelectedSize}
            onContinue={handleContinueToDetails}
          />
        )}
        {step === 2 && selectedStore && selectedSize && (
          <RaffleEntryDetailsStep
            raffle={raffle}
            selectedStore={selectedStore}
            selectedSize={selectedSize}
            onSubmitSuccess={() => setStep(3)}
            onTrendingProductsFetched={setTrendingProducts}
          />
        )}
        {step === 3 && (
          <RaffleEntryConfirmation
            raffle={raffle}
            selectedSize={selectedSize || undefined}
            selectedStore={selectedStore || undefined}
            trendingProducts={trendingProducts}
            onClose={onClose}
          />
        )}
      </ModalInner>
    </SideSheetModal>
  )
}

const ModalInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${colors.FC2_WHITE};
  overflow: hidden;
  min-height: 0;
`

const ModalHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid ${colors.FC2_LIGHTEST_GREY};
  flex-shrink: 0;
  background: ${colors.FC2_WHITE};
`

const HeaderSide = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const HeaderTitle = styled.h2`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0;
  color: ${colors.FC2_BLACK};
  margin: 0;
  text-align: center;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: ${colors.FC2_BLACK};
  padding: 0;
  width: 32px;
  height: 32px;
`

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.FC2_BLACK};
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(90deg);
`
